import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that shows/hides a product's pre-order policy notice based on whether the
 * currently selected variant is a pre-order (`resource.is_preorder`, added to the variant JSON
 * payload by snippets/variant-json-data.liquid - see blocks/buy-buttons.liquid for the shared
 * pre-order definition). The metafield content itself is product-level, not variant-specific, so
 * only visibility needs to react to variant changes, not the notice's text.
 *
 * Listens for the same `productSelect` event as product-sku-component, on the same container
 * shapes (product page, quick add, product card), so it stays in sync in all the contexts a
 * variant can be switched from.
 *
 * @extends {Component}
 */
class PreOrderPolicyComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles the product select event and toggles visibility for the newly selected variant.
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, resource } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        }

        if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        this.style.display = resource?.is_preorder === true ? 'block' : 'none';
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[pre-order-policy] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('pre-order-policy-notice')) {
  customElements.define('pre-order-policy-notice', PreOrderPolicyComponent);
}
